import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { Dropdown } from "@nova/component-dropdown"
import * as Icons from "@nova/component-icons"
import { ButtonSplit } from "@nova/ds-component-button-split"
import { useState } from "react"
import { DateRangePicker } from "features/filters/DateRangePicker"
import {
    DateRangeValue,
    dateRangeLabel,
    datePresetLabel,
    datePresets,
    isValidDateRange,
    resolveDateRange,
    toISODate,
} from "features/filters/dateRange"

export interface DateFilterValue {
    label: string
    from: string
    to: string
}

interface DateFilterProps {
    value: DateFilterValue | undefined
    onChange: (value: DateFilterValue | undefined) => void
}

const toFilterValue = (range: DateRangeValue): DateFilterValue => {
    const { start, end } = resolveDateRange(range)
    return {
        label: dateRangeLabel(range),
        from: toISODate(start),
        to: toISODate(end),
    }
}

const toRangeValue = (value: DateFilterValue): DateRangeValue => {
    const preset = datePresets.find((candidate) => datePresetLabel(candidate) === value.label)
    return preset ? { preset } : { from: value.from, to: value.to }
}

const DateFilterPicker = ({
    value,
    onApply,
}: {
    value: DateFilterValue | undefined
    onApply: (value: DateFilterValue) => void
}) => {
    const [pending, setPending] = useState<DateRangeValue>(() =>
        value ? toRangeValue(value) : { preset: "30d" }
    )

    return (
        <div className="flex flex-col gap-2">
            <DateRangePicker
                value={pending}
                onChange={(next) => {
                    if ("preset" in next) return onApply(toFilterValue(next))
                    setPending(next)
                }}
                applyOnPresetClick
            />

            <div className="flex justify-end gap-2 px-4 pb-1">
                <Dropdown.Close as={ButtonTertiary} title="Cancel" />
                <Dropdown.Close
                    as={ButtonPrimary}
                    title="Apply"
                    onClick={() => onApply(toFilterValue(pending))}
                    disabled={!isValidDateRange(pending)}
                />
            </div>
        </div>
    )
}

export const DateFilter = ({ value, onChange }: DateFilterProps) => {
    return (
        <Dropdown>
            <Dropdown.Trigger as="div" className="group">
                {value ? (
                    <ButtonSplit
                        label={value.label}
                        onRightClick={(e) => {
                            e.stopPropagation()
                            onChange(undefined)
                        }}
                    />
                ) : (
                    <ButtonTertiary
                        title="Date"
                        RightIcon={Icons.ArrowDropdown}
                        rightIconClassName="transition-transform group-data-[state=open]:rotate-180"
                        className="group-data-[state=open]:!border-gray-300 group-data-[state=open]:!text-gray-0"
                    />
                )}
            </Dropdown.Trigger>
            <Dropdown.Menu className="overflow-hidden">
                <DateFilterPicker value={value} onApply={onChange} />
            </Dropdown.Menu>
        </Dropdown>
    )
}
